import React from 'react';

import { ChevronLeftRounded, AccountCircle, Mic, ChevronRightRounded } from '@mui/icons-material';
import { Box, Stack, Typography, IconButton } from '@mui/material';
import { DateTime } from 'luxon';

import { SMSAutoResponse } from 'domains/content/types';
import theme from 'theme';

import {
    inputAreaStyles,
    inputFieldStyles,
    messageAreaStyles,
    messageBubbleStyles,
    messageDescriptorStyles,
    messageTextStyles,
    previewContainerStyles,
    previewHeaderStyles,
    receivedMessageBubbleStyles,
    sentMessageBubbleStyles,
} from './styles';

type Props = {
    autoResponses?: SMSAutoResponse[];
    formattedContent: string;
    senderNumber?: string;
};

const MessageBubble = ({ messageText, isSent = false }: { messageText: string; isSent?: boolean }) => {
    const bubbleStyles = isSent ? sentMessageBubbleStyles : receivedMessageBubbleStyles;
    return (
        <Box sx={{ display: 'flex', mb: 1, justifyContent: isSent ? 'flex-end' : 'flex-start' }}>
            <Box sx={{ ...messageBubbleStyles, ...bubbleStyles }}>
                <Typography variant="body2" sx={{ ...messageTextStyles, color: isSent ? 'white' : 'inherit' }}>
                    {messageText}
                </Typography>
            </Box>
        </Box>
    );
};

const SmsPreview = ({ autoResponses, formattedContent, senderNumber }: Props) => {
    const hasAutoResponses = !!autoResponses?.length;

    // We're only supporting one keyword right now, so we're grabbing the first in the array
    const renderAutoResponses = () =>
        autoResponses?.map((autoResponse) => (
            <>
                {!!autoResponse.keywords?.length && (
                    <>
                        <Typography variant="overline" sx={messageDescriptorStyles}>
                            If contact replies • {autoResponse.keywords[0]}
                        </Typography>
                        <MessageBubble messageText={autoResponse.keywords[0]} isSent />
                    </>
                )}
                {!!autoResponse.text?.length && <MessageBubble messageText={autoResponse.text} />}
            </>
        ));

    return (
        <Stack
            height="auto"
            justifySelf="center"
            sx={{ width: '100%', maxWidth: '320px', maxHeight: 'calc(100vh - 250px)' }}
        >
            <Stack sx={previewContainerStyles}>
                {/* Header */}
                <Stack direction="row" alignItems="center" sx={previewHeaderStyles}>
                    <ChevronLeftRounded fontSize="large" htmlColor={`${theme.palette.grey[400]}`} />
                    <Box flex={1} ml={-5} display="flex" justifyContent="center">
                        <Stack alignItems="center">
                            <AccountCircle fontSize="large" htmlColor={`${theme.palette.grey[400]}`} />
                            <Stack direction="row" alignItems="center">
                                <Typography variant="body2" color="text.primary">
                                    {senderNumber ? senderNumber : '+1 (999) 999-9999'}
                                </Typography>
                                <ChevronRightRounded fontSize="small" htmlColor={`${theme.palette.grey[400]}`} />
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>

                {/* Message Area */}
                <Stack sx={messageAreaStyles}>
                    <Typography variant="overline" sx={messageDescriptorStyles}>
                        Text Message • SMS
                        <br />
                        {DateTime.now().toFormat("ccc, MMM d 'at' h:mm a")}
                    </Typography>

                    <MessageBubble messageText={formattedContent} />
                    {hasAutoResponses && renderAutoResponses()}
                </Stack>

                {/* Input Area */}
                <Stack direction="row" sx={inputAreaStyles}>
                    <Stack direction="row" sx={inputFieldStyles}>
                        <Typography variant="body2" fontSize="14px" sx={{ color: `${theme.palette.grey[400]}` }}>
                            Text Message • SMS
                        </Typography>
                        <IconButton sx={{ color: `${theme.palette.grey[400]}` }}>
                            <Mic />
                        </IconButton>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default React.memo(SmsPreview);
